import { appBaseUrl } from '@core/http/api-url.util';

import { LEGAL_ENTITY_NAME } from '../../interview/legal/constants/legal.constants';

import { DEFAULT_META_DESCRIPTION, SITE_NAME } from './seo.constants';

const SUPPORT_EMAIL = 'hello@akalan.in';
const LINKEDIN_URL = 'https://www.linkedin.com/company/akalan-ai';
const TWITTER_URL = 'https://twitter.com/akalanai';

export function organizationId(base = appBaseUrl()): string {
  return `${base}/#organization`;
}

export function websiteId(base = appBaseUrl()): string {
  return `${base}/#website`;
}

/** Shared Organization + WebSite graph nodes for brand / AEO queries. */
export function buildOrganizationGraph(base = appBaseUrl()): Record<string, unknown>[] {
  const orgId = organizationId(base);
  const siteId = websiteId(base);
  const logoUrl = `${base}/akalan-lockup.png`;

  return [
    {
      '@type': 'Organization',
      '@id': orgId,
      name: SITE_NAME,
      legalName: LEGAL_ENTITY_NAME,
      url: `${base}/`,
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
        width: 1200,
        height: 630,
      },
      email: SUPPORT_EMAIL,
      sameAs: [LINKEDIN_URL, TWITTER_URL],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: SUPPORT_EMAIL,
        url: `${base}/contact`,
        availableLanguage: ['English', 'Hindi'],
        areaServed: 'IN',
      },
    },
    {
      '@type': 'WebSite',
      '@id': siteId,
      name: SITE_NAME,
      url: `${base}/`,
      description: DEFAULT_META_DESCRIPTION,
      inLanguage: ['en-IN', 'hi-IN'],
      publisher: { '@id': orgId },
    },
  ];
}

export function buildAboutStructuredData(): Record<string, unknown> {
  const base = appBaseUrl();
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ...buildOrganizationGraph(base),
      {
        '@type': 'AboutPage',
        '@id': `${base}/about#webpage`,
        url: `${base}/about`,
        name: 'About Akalan',
        description:
          'Akalan builds AI voice mock interviews, ATS resume scoring, and JD-tailored rewrites for Indian job seekers, colleges, and hiring teams.',
        isPartOf: { '@id': websiteId(base) },
        about: { '@id': organizationId(base) },
        inLanguage: 'en-IN',
      },
    ],
  };
}

export function buildContactStructuredData(): Record<string, unknown> {
  const base = appBaseUrl();
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ...buildOrganizationGraph(base),
      {
        '@type': 'ContactPage',
        '@id': `${base}/contact#webpage`,
        url: `${base}/contact`,
        name: 'Contact Akalan',
        description:
          'Email hello@akalan.in or book a demo for Akalan product support, campus partnerships, and company hiring programs.',
        isPartOf: { '@id': websiteId(base) },
        about: { '@id': organizationId(base) },
        inLanguage: 'en-IN',
      },
    ],
  };
}
